<template>
  <v-container class="py-md-8">
    <h2 class="text-h2 flex-fill px-3 mb-6">Surveys</h2>
    <v-data-table
      :headers="headers"
      :items="items"
      item-key="name"
      class="elevation-1"
      :search="search"
      :loading="loading"
      :custom-sort="customSort"
    >
      <template v-slot:top>
        <div class="d-flex align-center pa-4">
          <v-text-field
            rounded
            flat
            filled
            type="search"
            v-model="search"
            dense
            hide-details=""
            placeholder="Search Surveys"
            class="mr-2 flex-fill"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                fab
                color="primary"
                small
                elevation="0"
                :to="{ name: 'editor', params: { id: 'new' } }"
                ><font-awesome-icon icon="plus"></font-awesome-icon
              ></v-btn>
            </template>
            <span>Add Survey</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              :to="{ name: 'editor', params: { id: item.id } }"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              :to="{
                name: 'editor',
                params: { id: 'new' },
                query: { clone: item.id },
              }"
            >
              <v-icon small>$copy</v-icon>
            </v-btn>
          </template>
          <span>Copy</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              :to="{ name: 'surveyCodes', params: { id: item.id } }"
            >
              <v-icon small>$key</v-icon>
            </v-btn>
          </template>
          <span>Access Keys</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              :to="{
                name: 'reporting',
                params: { id: item.id },
              }"
            >
              <v-icon small>$chart</v-icon>
            </v-btn>
          </template>
          <span>Reporting</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              class="mr-2"
              @click="downloadResponses(item)"
            >
              <v-icon small>$download</v-icon>
            </v-btn>
          </template>
          <span>Download Responses</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon @click="confirmDelete(item)">
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="openConfirmDelete" max-width="400px">
      <v-card>
        <dialog-title
          v-model="openConfirmDelete"
          title="Confirm Delete"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete survey:
                <strong class="red--text darken-1 font-weight-bold">{{
                  item.name
                }}</strong
                >. <br /><br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirmDelete = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteSurvey"
              >
                Yes, delete
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import surveyService from "../../services/surveyService";
import compare from "compare-func";
import dateTimeService from "../../services/dateTimeService";
import DialogTitle from "../../components/DialogTitle.vue";

const newItem = () => ({
  //TBD
});

export default {
  components: {
    DialogTitle,
  },
  data() {
    return {
      search: "",
      items: [],
      item: newItem(),
      openConfirmDelete: false,
      pendingStateChanges: 0,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: "Name",
          value: "name",
          cellClass: "w-100",
        },
        {
          text: "Status",
          value: "status",
          cellClass: "text-capitalize",
          class: "text-no-wrap",
        },
        {
          text: "Start Date",
          value: "formattedStartDate",
          cellClass: "text-no-wrap",
          class: "text-no-wrap",
        },
        {
          text: "End Date",
          value: "formattedEndDate",
          cellClass: "text-no-wrap",
          class: "text-no-wrap",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          cellClass: "w-0 text-no-wrap",
          align: "center",
        },
      ];
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    tenantId() {
      return this.$store.state.tenantId;
    },
  },
  methods: {
    filter(value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === "string" &&
        value
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) !== -1
      );
    },
    load() {
      this.pendingStateChanges++;

      return surveyService
        .getAll(this.tenantId)
        .then(
          (result) => {
            this.items = result.map((x) => {
              x.formattedStartDate =
                dateTimeService.formatDateTime(x.startDate) ?? "--";
              x.formattedEndDate =
                dateTimeService.formatDateTime(x.endDate) ?? "--";

              return x;
            });
          },
          () => {
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading surveys. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    customSort(items, index, isDesc) {
      let sortOn = index;
      if (sortOn === "formattedStartDate") sortOn = "startDate";
      if (sortOn === "formattedEndDate") sortOn = "endDate";

      items.sort(compare(sortOn));

      return !isDesc ? items : items.reverse();
    },
    confirmDelete(item) {
      this.item = item;
      this.openConfirmDelete = true;
    },
    deleteSurvey() {
      this.openConfirmDelete = false;
      this.pendingStateChanges++;

      return surveyService
        .deleteSurvey(this.tenantId, this.item.id)
        .then(this.load, () => {
          this.$store.dispatch(
            "notifications/error",
            "An unexpected error occurred while deleting survey. Please try again later."
          );
        })
        .finally(() => this.pendingStateChanges--);
    },
    downloadResponses(survey) {
      this.pendingStateChanges++;
      return surveyService
        .downloadResponses(this.tenantId, survey.id, [])
        .then(
          () => {
            this.$store.dispatch(
              "notifications/success",
              `Export has started, and once completed will appear in the azure FTP.`
            );
            // const url = window.URL.createObjectURL(new Blob([response]));
            // const link = document.createElement("a");
            // link.href = url;
            // link.setAttribute("download", `${surveyId}_Responses.csv`); //or any other extension
            // document.body.appendChild(link);
            // link.click();
          },
          (error) => {
            console.warn(error);
              this.$store.dispatch(
              "notifications/error",
              `Export has failed to start.`
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style>
.w-0 {
  width: 0;
}
</style>
